import { Assert } from '@cotera/utilities';

/**
 * Query key that mirrors the run currently open in the editor's artifact panel.
 * A run drilled into the panel lives only in in-memory panel state, so without
 * this the URL couldn't name it — you couldn't share, bookmark, or reload back
 * onto a specific run. The value is just the run id; the workflow is already in
 * the path.
 */
export const RUN_URL_PARAM = 'run';

/**
 * Absolute link to a run, for the Share button on the run detail header.
 *
 * Points at the standalone `/runs/:runId` route rather than the editor URL the
 * sharer is on. `?run=` only names the run for the *panel* the sharer already
 * has open: `/workflows/:id` is a legacy route that redirects into a freshly
 * seeded chat, and that redirect rebuilds the query string from the seed
 * strategy — so a shared `?run=` never survives the hop and the recipient lands
 * on the workflow with no run. The standalone route renders the same run detail
 * for anyone who can see the workflow.
 */
export function workflowRunShareUrl(input: {
  /** Usually `window.location.origin`; trailing slash tolerated. */
  origin: string;
  orgSlug: string;
  workflowId: string;
  runId: string;
}): string {
  const { origin, orgSlug, workflowId, runId } = input;
  const path = [orgSlug, 'workflows', workflowId, 'runs', runId]
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${origin.replace(/\/+$/, '')}/${path}`;
}

/** What the panel/URL should do to become consistent again. */
export type RunUrlSyncAction =
  | { kind: 'none' }
  /** Open this run in the panel (the URL named a run the panel isn't showing). */
  | { kind: 'open-run'; runId: string }
  /** Leave the run view — the URL dropped its run (e.g. browser back). */
  | { kind: 'clear-panel' }
  /**
   * Write `runId` into the URL to match the panel. An empty string clears the
   * param (the url adapter deletes a key whose value is `''`), which is how
   * closing a run scrubs `?run=` from the address bar.
   */
  | { kind: 'write-url'; runId: string };

/**
 * Reconcile the run open in the panel with the `?run=` URL param, in both
 * directions, without the two chasing each other.
 *
 * The caller remembers what each side was last reconciled to (`prevUrlRun`,
 * `prevOpenRun`); comparing the current values against them tells us *which side
 * moved* — the user opening/closing a run in the panel, or the URL changing under
 * us (a shared link, a reload, browser back/forward). Whichever moved is the
 * source of truth, so a run closing can't be misread as "the URL still wants this
 * run" and immediately reopen it.
 *
 * `urlRun`/`openRun`/`prevUrlRun`/`prevOpenRun` are all "" when absent, never
 * null, so equality is a plain string compare.
 */
export function reconcileRunUrlSync(input: {
  urlRun: string;
  openRun: string;
  prevUrlRun: string;
  prevOpenRun: string;
}): RunUrlSyncAction {
  const { urlRun, openRun, prevUrlRun, prevOpenRun } = input;

  if (urlRun === openRun) {
    return { kind: 'none' };
  }

  const urlChanged = urlRun !== prevUrlRun;
  const openChanged = openRun !== prevOpenRun;

  if (urlChanged && !openChanged) {
    // The URL moved (a shared link, a reload, browser back/forward). Follow it:
    // open the named run, or exit the run view when it cleared.
    return urlRun === ''
      ? { kind: 'clear-panel' }
      : { kind: 'open-run', runId: urlRun };
  }

  if (openChanged && !urlChanged) {
    // The panel moved (the user opened, switched, or closed a run). The URL
    // follows — including clearing when they backed out, which is what lets the
    // breadcrumb / back arrow leave this page instead of being reopened.
    return { kind: 'write-url', runId: openRun };
  }

  // Neither ref moved yet they disagree: the first reconcile after mount with a
  // deep-linked run. Treat the URL as the source of truth. (Opening no-ops until
  // an artifact is focused; the effect re-runs when one is, so the run still
  // opens.)
  return urlRun !== ''
    ? { kind: 'open-run', runId: urlRun }
    : { kind: 'write-url', runId: openRun };
}

/** Exhaustiveness helper so callers can't forget a new action kind. */
export function assertNeverRunUrlSyncAction(action: never): never {
  return Assert.unreachable(action);
}
